import { convertRestFolderIdToEWSFolderId } from 'owa-favorites/lib/utils/ewsRestFolderIdConverter';
import {
    OutlookFavoriteKind,
    OutlookFavoriteServiceDataType,
    FavoriteData,
    FavoriteFolderData,
    FavoritePublicFolderData,
    FavoritePersonaData,
    FavoriteGroupData,
    FavoriteCategoryData,
    FavoritePrivateDistributionListData,
    FavoriteDataClient,
    FavoriteSearchData,
} from 'owa-favorites-types';
import { PrivateDistributionListMember } from 'owa-persona';

export const SEARCH_FOLDER_ID_KEY = 'SearchFolderId';
export const PERSONA_ID_KEY = 'PersonaId';
export const MAIN_EMAIL_ADDRESS_KEY = 'MainEmailAddress';
export const EMAIL_ADDRESSES_KEY = 'EmailAddresses';
export const CONTACT_DISPLAYNAME_KEY = 'ContactDisplayName';
export const CONTACT_EMAIL_ADDRESS_KEY = 'ContactMainEmailAddress';
export const PDL_ID_KEY = 'PdlId';
export const MEMBER_NAMES_KEY = 'MemberNames';
export const GROUP_SMTP_KEY = 'EmailAddress';
export const GROUP_EXTERNAL_DIRCTORY_OBJECT_ID_KEY = 'ExtDirId';

/**
 * Retrieves the single value setting for the provided key from the provided serviceData object
 * or undefined if the key is not found
 * @param serviceData the serviceData object
 * @param key the key
 */
export function getSingleValueSettingValueForKey(
    serviceData: OutlookFavoriteServiceDataType,
    key: string
): string {
    const settings =
        serviceData.SingleValueSettings &&
        serviceData.SingleValueSettings.filter(data => data.Key == key);
    return settings && settings.length === 1 ? settings[0].Value : undefined;
}

/**
 * Retrieves the multi value setting for the provided key from the provided serviceData object
 * or [] if the key is not found
 * @param serviceData the serviceData object
 * @param key the key
 */
export function getMultiValueSettingValueForKey(
    serviceData: OutlookFavoriteServiceDataType,
    key: string
): string[] {
    const settings =
        serviceData.MultiValueSettings &&
        serviceData.MultiValueSettings.filter(data => data.Key == key);
    return settings && settings.length === 1 ? settings[0].Values : [];
}

export function convertServiceResponseToFavoriteData(
    serviceData: OutlookFavoriteServiceDataType,
    favoriteKind: OutlookFavoriteKind
): FavoriteData {
    let favoriteData: FavoriteData;

    switch (favoriteKind) {
        case 'folder':
            const favoriteFolderData: FavoriteFolderData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'folder',
                displayName: serviceData.DisplayName,
                folderId: convertRestFolderIdToEWSFolderId(
                    serviceData.SingleValueSettings[0].Value
                ),
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
            };
            favoriteData = favoriteFolderData;
            break;

        case 'persona':
            const searchFolderId = getSingleValueSettingValueForKey(
                serviceData,
                SEARCH_FOLDER_ID_KEY
            );

            // Conversion necessary because OWA uses OWS (and EWS Ids) for managing folders
            const convertedSearchFolderId = searchFolderId
                ? convertRestFolderIdToEWSFolderId(searchFolderId)
                : null;

            const allEmailAddresses = getMultiValueSettingValueForKey(
                serviceData,
                EMAIL_ADDRESSES_KEY
            );
            const mailEmailAddress = getSingleValueSettingValueForKey(
                serviceData,
                MAIN_EMAIL_ADDRESS_KEY
            );
            const personaId = getSingleValueSettingValueForKey(serviceData, PERSONA_ID_KEY);

            const contactDisplayName = getSingleValueSettingValueForKey(
                serviceData,
                CONTACT_DISPLAYNAME_KEY
            );
            const contactMainEmailAddress = getSingleValueSettingValueForKey(
                serviceData,
                CONTACT_EMAIL_ADDRESS_KEY
            );

            const favoritePersonaData: FavoritePersonaData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'persona',
                displayName: serviceData.DisplayName,
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
                searchFolderId: convertedSearchFolderId,
                allEmailAddresses: allEmailAddresses,
                mainEmailAddress: mailEmailAddress,
                isSearchFolderPopulated: !!convertedSearchFolderId,
                personaId: personaId,
                contactDisplayName: contactDisplayName,
                contactMainEmailAddress: contactMainEmailAddress,
                isSyncUpdateDone: false,
            };
            favoriteData = favoritePersonaData;
            break;

        case 'privatedistributionlist':
            const pdlSearchFolderId = getSingleValueSettingValueForKey(
                serviceData,
                SEARCH_FOLDER_ID_KEY
            );

            // Conversion necessary because OWA uses OWS (and EWS Ids) for managing folders
            const pdlConvertedSearchFolderId = pdlSearchFolderId
                ? convertRestFolderIdToEWSFolderId(pdlSearchFolderId)
                : null;

            const pdlId = getSingleValueSettingValueForKey(serviceData, PDL_ID_KEY);

            const owsPersonaId = getSingleValueSettingValueForKey(serviceData, 'PersonaId');
            const convertedOwsPersonaId = owsPersonaId
                ? convertRestFolderIdToEWSFolderId(owsPersonaId)
                : null;

            const memberEmails = getMultiValueSettingValueForKey(serviceData, EMAIL_ADDRESSES_KEY);
            const memberNames = getMultiValueSettingValueForKey(serviceData, MEMBER_NAMES_KEY);

            const members: PrivateDistributionListMember[] = [];

            for (let index = 0; index < memberEmails.length; index++) {
                members.push({
                    name: memberNames[index],
                    emailAddress: memberEmails[index],
                });
            }

            const favoritePrivateDistributionListData: FavoritePrivateDistributionListData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'privatedistributionlist',
                displayName: serviceData.DisplayName,
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
                searchFolderId: pdlConvertedSearchFolderId,
                isSearchFolderPopulated: !!pdlConvertedSearchFolderId,
                pdlId: pdlId,
                members: members,
                owsPersonaId: convertedOwsPersonaId,
            };

            favoriteData = favoritePrivateDistributionListData;

            break;

        case 'group':
            const favoriteGroupData: FavoriteGroupData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'group',
                displayName: serviceData.DisplayName,
                groupId: serviceData.SingleValueSettings[0].Value.toLowerCase(),
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
                groupSmtp: getSingleValueSettingValueForKey(
                    serviceData,
                    GROUP_SMTP_KEY
                ).toLowerCase(),
                groupExternalDirectoryObjectId: getSingleValueSettingValueForKey(
                    serviceData,
                    GROUP_EXTERNAL_DIRCTORY_OBJECT_ID_KEY
                ),
            };

            favoriteData = favoriteGroupData;
            break;

        case 'category':
            const favoriteCategoryData: FavoriteCategoryData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'category',
                displayName: serviceData.DisplayName,
                categoryId: serviceData.SingleValueSettings[0].Value,
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
            };

            favoriteData = favoriteCategoryData;
            break;

        case 'search':
            const favoriteSearchData: FavoriteSearchData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'search',
                displayName: serviceData.DisplayName,
                searchQuery: serviceData.SingleValueSettings[0].Value,
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
            };

            favoriteData = favoriteSearchData;
            break;

        case 'publicFolder':
            const favoritePublicFolderData: FavoritePublicFolderData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: 'publicFolder',
                displayName: serviceData.DisplayName,
                publicFolderId: getSingleValueSettingValueForKey(serviceData, 'FolderId'),
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
            };

            favoriteData = favoritePublicFolderData;
            break;

        default:
            const favoriteDataLocal: FavoriteData = {
                treeType: 'favorites',
                favoriteId: serviceData.Id,
                type: serviceData.Type,
                displayName: serviceData.DisplayName,
                client: serviceData.Client as FavoriteDataClient,
                lastModifiedTime: serviceData.LastModifiedTime,
            };

            favoriteData = favoriteDataLocal;
            break;
    }

    return favoriteData;
}
